import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { AlertCircle, RotateCcw } from "lucide-react";
import { createFollowupQuestion } from "../../../../api/common";
import type { Response, ResponseStatus } from "./ReviewResponseModel.types";
import { RiEdit2Fill } from "react-icons/ri";

interface ResponseCardProps {
  response: Response;
  status: ResponseStatus | undefined;
  onUpdateStatus: (updates: Partial<ResponseStatus>) => void;
  onShowAIEvaluation: () => void;
  onRegenerateQuestions: () => void;
  isRegenerating: boolean;
}

const ResponseCard: React.FC<ResponseCardProps> = ({
  response,
  status,
  onUpdateStatus,
  onShowAIEvaluation,
  onRegenerateQuestions,
  isRegenerating,
}) => {
  const { id } = useParams();
  const [customQuestionInput, setCustomQuestionInput] = useState("");
  const [editingQuestionIndex, setEditingQuestionIndex] = useState<
    number | null
  >(null);
  const [editingCustomIndex, setEditingCustomIndex] = useState<
    number | null
  >(null);
  const [editText, setEditText] = useState("");
  const [isFollowupLoading, setIsFollowupLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  const currentSelection = status?.userSelection || null;
  const showFollowUp = currentSelection ;
  // Determine card state - only show colored background when user has made a selection
  const cardState = currentSelection;

  const handleRadioChange = async (value: "Satisfied" | "Needs Follow-up") => {
    if (value === "Satisfied") {
      setHasError(false);
      onUpdateStatus({
        userSelection: "Satisfied",
        followUpReason: response.aiEvaluation.reason || "",
        aiGeneratedQuestions: [],
        selectedAIQuestion: null,
        customQuestions: [],
        questionCode: undefined,
      });
        // console.log(response);
      return;
    }

    // Needs Follow-up
    try {
      setIsFollowupLoading(true);

      const payload = {
        auditId: id,
        questionCode: response.questionCode,
      };
      const apiResponse = await createFollowupQuestion(payload);

      onUpdateStatus({
        userSelection: "Needs Follow-up",
        followUpReason: apiResponse.reason || "",
        aiGeneratedQuestions: apiResponse.generatedFollowupQuestions || [],
        selectedAIQuestion: apiResponse.generatedFollowupQuestions?.[0] || null,
        customQuestions: [],
        questionCode: response.questionCode,
      });
    } catch (error: any) {
      console.error("Failed to fetch follow-up questions", error);
      if (error.response?.status === 400) {
        setHasError(true);
      }
      onUpdateStatus({
        userSelection: "Needs Follow-up",
        followUpReason: "",
        aiGeneratedQuestions: [],
        selectedAIQuestion: null,
        customQuestions: [],
        questionCode: response.questionCode,
      });
    } finally {
      setIsFollowupLoading(false);
    }
  };

  const handleRetry = async () => {
    try {
      setHasError(false);
      setIsFollowupLoading(true);

      const payload = {
        auditId: id,
        questionCode: response.questionCode,
      };
      const apiResponse = await createFollowupQuestion(payload);
      onUpdateStatus({
        userSelection: "Needs Follow-up",
        followUpReason: apiResponse.reason || "",
        aiGeneratedQuestions: apiResponse.generatedFollowupQuestions || [],
        selectedAIQuestion: apiResponse.generatedFollowupQuestions?.[0] || null,
        questionCode: response.questionCode,
      });
    } catch (error: any) {
      console.error("Failed to fetch follow-up questions on retry", error);
      if (error.response?.status === 400) {
        setHasError(true);
      }
    } finally {
      setIsFollowupLoading(false);
    }
  };

  const handleAddCustomQuestion = () => {
    if (customQuestionInput.trim()) {
      onUpdateStatus({
        selectedAIQuestion: customQuestionInput.trim(),
        customQuestions: [
          ...(status?.customQuestions || []),
          customQuestionInput.trim(),
        ],
      });
      setCustomQuestionInput("");
    }
  };

  const handleDeleteCustomQuestion = (index: number) => {
    const updatedCustomQuestions = [...(status?.customQuestions || [])];
    const questionToRemove = updatedCustomQuestions[index];
    updatedCustomQuestions.splice(index, 1);

    const newSelectedQuestion =
      status?.selectedAIQuestion === questionToRemove
        ? null
        : status?.selectedAIQuestion;

    onUpdateStatus({
      customQuestions: updatedCustomQuestions,
      selectedAIQuestion: newSelectedQuestion,
    });
  };

  const handleToggleAIQuestion = (question: string) => {
    onUpdateStatus({ selectedAIQuestion: question });
  };

  const handleDeleteAIQuestion = (index: number) => {
    const aiQuestions = [...(status?.aiGeneratedQuestions || [])];
    const removedQuestion = aiQuestions[index];
    aiQuestions.splice(index, 1);

    const newSelectedQuestion =
      status?.selectedAIQuestion === removedQuestion
        ? null
        : status?.selectedAIQuestion;

    onUpdateStatus({
      aiGeneratedQuestions: aiQuestions,
      selectedAIQuestion: newSelectedQuestion,
    });
  };

  const handleEditAIQuestion = (index: number) => {
    const aiQuestions = [...(status?.aiGeneratedQuestions || [])];
    const oldQuestion = aiQuestions[index];
    aiQuestions[index] = editText;

    const newSelectedQuestion =
      status?.selectedAIQuestion === oldQuestion
        ? editText
        : status?.selectedAIQuestion;

    onUpdateStatus({
      aiGeneratedQuestions: aiQuestions,
      selectedAIQuestion: newSelectedQuestion,
    });

    setEditingQuestionIndex(null);
    setEditText("");
  };

  const handleEditCustomQuestion = (index: number) => {
    const customQuestions = [...(status?.customQuestions || [])];
    const oldQuestion = customQuestions[index];
    customQuestions[index] = editText;

    const newSelectedQuestion =
      status?.selectedAIQuestion === oldQuestion
        ? editText
        : status?.selectedAIQuestion;

    onUpdateStatus({
      customQuestions,
      selectedAIQuestion: newSelectedQuestion,
    });

    setEditingCustomIndex(null);
    setEditText("");
  };

  return (
    <div
      className={`review-response-card ${cardState === "Satisfied" ? "satisfied" : cardState === "Needs Follow-up" ? "needs-followup" : ""}`}
    >
      <div className="review-response-card-header">
        <div className="review-responder-info">
          <div className="review-responder-name">{response.responderName}</div>
          <div className="review-responded-at">
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              style={{
                display: "inline-block",
                marginRight: "4px",
                verticalAlign: "middle",
              }}
            >
              <circle
                cx="7"
                cy="7"
                r="6"
                stroke="currentColor"
                strokeWidth="1.2"
                fill="none"
              />
              <path
                d="M7 3.5v3.5l2.5 1.5"
                stroke="currentColor"
                strokeWidth="1.2"
                fill="none"
                strokeLinecap="round"
              />
            </svg>
            Responded: {response.respondedAt}
          </div>
        </div>
      </div>

      <div className="review-response-text">{response.responseText}</div>

      {response.artifacts && response.artifacts.length > 0 && (
        <div className="review-uploaded-artifacts">
          <div className="review-artifacts-label">
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              style={{
                display: "inline-block",
                marginRight: "6px",
                verticalAlign: "middle",
              }}
            >
              <path
                d="M9 1H3.5C2.67 1 2 1.67 2 2.5v11c0 .83.67 1.5 1.5 1.5h9c.83 0 1.5-.67 1.5-1.5V5.5L9 1z"
                stroke="currentColor"
                strokeWidth="1.5"
                fill="none"
              />
              <path
                d="M9 1v4.5h4.5"
                stroke="currentColor"
                strokeWidth="1.5"
                fill="none"
              />
            </svg>
            Uploaded Artifacts
          </div>
          <div className="review-artifacts-list">
            {response.artifacts.map((artifact) => (
              <div key={artifact.id} className="review-artifact-item">
                <div className="review-artifact-file">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    className="review-file-icon"
                  >
                    <path
                      d="M9 1H3.5C2.67 1 2 1.67 2 2.5v11c0 .83.67 1.5 1.5 1.5h9c.83 0 1.5-.67 1.5-1.5V5.5L9 1z"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      fill="none"
                    />
                    <path
                      d="M9 1v4.5h4.5"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      fill="none"
                    />
                  </svg>
                  <span className="review-artifact-name">
                    {artifact.fileName}
                  </span>
                </div>
                <button className="review-download-button" title="Download">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path
                      d="M8 11V3M8 11L5 8M8 11l3-3M2 13h12"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="review-response-actions">
        <div className="review-response-evaluation-labels">
          <div className="review-evaluation-section-label">
            AI Response Evaluation
          </div>
          <div className="review-evaluation-section-label">
            Your Response<span className="review-required-asterisk">*</span>
          </div>
        </div>

        <div className="review-response-evaluation-row">
          <div className="review-ai-evaluation-section">
            <button
              className={`review-ai-evaluation-badge ${response.aiEvaluation.status === "Satisfied" ? "satisfied" : "not-satisfied"}`}
              onClick={onShowAIEvaluation}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
                className="review-sparkle-icon"
              >
                <path
                  d="M7 1L8.5 5.5L13 7L8.5 8.5L7 13L5.5 8.5L1 7L5.5 5.5L7 1Z"
                  fill="currentColor"
                  stroke="currentColor"
                  strokeWidth="1"
                  strokeLinejoin="round"
                />
              </svg>
              <span>
                {response.aiEvaluation.status === "Satisfied"
                  ? "Satisfied"
                  : "Follow-up Recommended"}
              </span>
            </button>
          </div>

          <div className="review-your-response-section">
            <div className="review-radio-group">
              <label
                className={`review-radio-label satisfied-option ${currentSelection === "Satisfied" ? "selected" : ""}`}
              >
                <input
                  type="radio"
                  name={`response-${response.id}`}
                  checked={currentSelection === "Satisfied"}
                  onChange={() => handleRadioChange("Satisfied")}
                  disabled={isFollowupLoading}
                />
                <span className="review-radio-text">Satisfied</span>
              </label>

              <label
                className={`review-radio-label followup-option ${
                  currentSelection === "Needs Follow-up" ? "selected" : ""
                }`}
              >
                <input
                  type="radio"
                  name={`response-${response.id}`}
                  checked={currentSelection === "Needs Follow-up"}
                  onChange={() => handleRadioChange("Needs Follow-up")}
                  disabled={isFollowupLoading}
                />

                {isFollowupLoading ? (
                  <span className="review-radio-loader">
                    <span className="spinner" /> Fetching follow-up…
                  </span>
                ) : (
                  <span className="review-radio-text">Needs Followup</span>
                )}
              </label>
            </div>
          </div>
        </div>
      </div>

      {showFollowUp === "Needs Follow-up" ? (
        <div className="review-follow-up-section">
          <div className="review-follow-up-reason">
            <label className="review-follow-up-label">Reason*</label>
            <textarea
              className="review-reason-textarea"
              placeholder="Provide a reason for follow-up..."
              value={status?.followUpReason || ""}
              onChange={(e) =>
                onUpdateStatus({ followUpReason: e.target.value })
              }
              rows={3}
            />
          </div>

          {hasError && !isFollowupLoading ? (
            <div className="review-error-wrapper">
              <div className="review-error-card">
                <div className="review-error-icon-wrapper">
                  <AlertCircle className="review-error-icon" />
                </div>
                <div className="review-error-text">
                  <h3>We couldn't load AI-suggested questions</h3>
                  <p>No worries, this happens sometimes. Please try again.</p>
                  <p>
                    You may continue adding your own questions without any interruption.
                  </p>
                </div>
                <RotateCcw
                  className="review-error-retry-icon"
                  onClick={handleRetry}
                />
              </div>
            </div>
          ) : null}

          <div className="review-ai-generated-questions">
            <div className="review-section-header">
              <span className="review-section-title">
                AI Generated Questions (
                {status?.aiGeneratedQuestions?.length || 0}
                )
              </span>
              <button
                className="review-regenerate-button"
                onClick={onRegenerateQuestions}
                disabled={isRegenerating}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 14 14"
                  fill="none"
                  className={isRegenerating ? "review-spinning" : ""}
                >
                  <path
                    d="M13 2v4h-4M1 12V8h4M12.364 5.364A6 6 0 1 0 13 7"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    fill="none"
                  />
                </svg>
                {isRegenerating ? "Regenerating..." : "Regenerate"}
              </button>
            </div>

            {((status?.aiGeneratedQuestions?.length || 0) > 0 ||
              (status?.customQuestions?.length || 0) > 0) && (
              <div className="review-questions-list">
                {(status?.aiGeneratedQuestions || []).map((question, index) => (
                  <div key={`ai-${index}`} className="review-question-item-editable">
                    <input
                      type="radio"
                      name={`ai-question-${response.id}`}
                      checked={status?.selectedAIQuestion === question}
                      onChange={() => handleToggleAIQuestion(question)}
                      className="review-question-radio"
                    />

                    {editingQuestionIndex === index ? (
                      <div className="review-edit-question-container">
                        <input
                          type="text"
                          value={editText}
                          onChange={(e) => setEditText(e.target.value)}
                          className="review-edit-question-input"
                          autoFocus
                        />
                        <button
                          className="review-save-edit-button"
                          onClick={() => handleEditAIQuestion(index)}
                        >
                          Save
                        </button>
                        <button
                          className="review-cancel-edit-button"
                          onClick={() => {
                            setEditingQuestionIndex(null);
                            setEditText("");
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <>
                        <span className="review-question-text">
                          {question}
                        </span>
                        <div className="review-question-actions">
                          <button
                            className="review-edit-icon-button"
                            onClick={() => {
                              setEditingQuestionIndex(index);
                              setEditText(question);
                            }}
                            title="Edit question"
                          >
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 14 14"
                              fill="none"
                            >
                              <path
                                d="M10 1l3 3-8 8H2v-3l8-8z"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                fill="none"
                              />
                            </svg>
                          </button>
                          <button
                            className="review-delete-icon-button"
                            onClick={() => handleDeleteAIQuestion(index)}
                            title="Delete question"
                          >
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 14 14"
                              fill="none"
                            >
                              <path
                                d="M1 3.5h12M5.5 1h3M5.5 6v4.5M8.5 6v4.5M2.5 3.5l.5 8.5c0 .55.45 1 1 1h6c.55 0 1-.45 1-1l.5-8.5"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                fill="none"
                              />
                            </svg>
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                ))}

                {(status?.customQuestions || []).map((question, index) => (
                  <div key={`custom-${index}`} className="review-question-item-editable">
                    <input
                      type="radio"
                      name={`ai-question-${response.id}`}
                      checked={status?.selectedAIQuestion === question}
                      onChange={() => handleToggleAIQuestion(question)}
                      className="review-question-radio"
                    />

                    {editingCustomIndex === index ? (
                      <div className="review-edit-question-container">
                        <input
                          type="text"
                          value={editText}
                          onChange={(e) => setEditText(e.target.value)}
                          className="review-edit-question-input"
                          autoFocus
                        />
                        <button
                          className="review-save-edit-button"
                          onClick={() => handleEditCustomQuestion(index)}
                        >
                          Save
                        </button>
                        <button
                          className="review-cancel-edit-button"
                          onClick={() => {
                            setEditingCustomIndex(null);
                            setEditText("");
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <>
                        <span className="review-question-text">
                          {question} 
                          &nbsp;&nbsp;
                          <RiEdit2Fill/>
                        </span>
                        <div className="review-question-actions">
                          <button
                            className="review-edit-icon-button"
                            onClick={() => {
                              setEditingCustomIndex(index);
                              setEditText(question);
                            }}
                            title="Edit question"
                          >
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 14 14"
                              fill="none"
                            >
                              <path
                                d="M10 1l3 3-8 8H2v-3l8-8z"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                fill="none"
                              />
                            </svg>
                          </button>
                          <button
                            className="review-delete-icon-button"
                            onClick={() => handleDeleteCustomQuestion(index)}
                            title="Delete question"
                          >
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 14 14"
                              fill="none"
                            >
                              <path
                                d="M1 3.5h12M5.5 1h3M5.5 6v4.5M8.5 6v4.5M2.5 3.5l.5 8.5c0 .55.45 1 1 1h6c.55 0 1-.45 1-1l.5-8.5"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                fill="none"
                              />
                            </svg>
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="review-add-custom-question">
            <label className="review-follow-up-label">
              Add Custom Question
            </label>
            <div className="review-custom-question-input-group">
              <input
                type="text"
                className="review-custom-question-input"
                placeholder="Type a custom question..."
                value={customQuestionInput}
                onChange={(e) => setCustomQuestionInput(e.target.value)}
                onKeyPress={(e) =>
                  e.key === "Enter" && handleAddCustomQuestion()
                }
              />
              <button
                className="review-add-question-button"
                onClick={handleAddCustomQuestion}
                disabled={!customQuestionInput.trim()}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M8 3v10M3 8h10"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      ) : 
      showFollowUp === "Satisfied" && (
        <div className="review-follow-up-section">
          <div className="review-follow-up-reason">
            <label className="review-follow-up-label">Reason</label>
            <textarea
              className="review-reason-textarea"
              placeholder="Provide a reason..."
              value={status?.followUpReason || ""}
              onChange={(e) =>
                onUpdateStatus({ followUpReason: e.target.value })
              }
              rows={3}
            />
          </div>
        </div>
      )
      }
    </div>
  );
};

export default ResponseCard;
